/* Crystal Lake Leather Arts — storefront behavior
 * Ports the interactivity the React prototype handled in component state:
 * cart drawer + AJAX cart, search overlay (predictive), mobile menu,
 * PDP variant/qty selection, and the light/dark theme toggle.
 * Vanilla JS, no build step — mirrors the no-bundler spirit of the prototype.
 */
(function () {
  'use strict';

  const money = (cents) =>
    (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(cents, window.CLLA && window.CLLA.moneyFormat)
      : '$' + (cents / 100).toFixed(2);

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  /* ─────────────── Drawer helpers ─────────────── */
  function openDrawer(el) {
    if (!el) return;
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
  }
  function closeDrawer(el) {
    if (!el) return;
    el.setAttribute('aria-hidden', 'true');
    if (!$$('[data-drawer][aria-hidden="false"]').length) {
      document.body.classList.remove('drawer-open');
    }
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(document.getElementById(opener.getAttribute('data-drawer-open')));
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer(closer.closest('[data-drawer]'));
    }
    if (e.target.matches('[data-drawer] .drawer-scrim, .cart-overlay, .search-overlay')) {
      closeDrawer(e.target.closest('[data-drawer]'));
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') $$('[data-drawer][aria-hidden="false"]').forEach(closeDrawer);
  });

  /* ─────────────── Cart (AJAX) ─────────────── */
  async function fetchCart() {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return r.json();
  }

  function renderCartCount(cart) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.toggleAttribute('hidden', cart.item_count === 0);
    });
  }

  async function refreshCartDrawer() {
    const cart = await fetchCart();
    renderCartCount(cart);
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return cart;
    try {
      const html = await (await fetch(window.location.pathname + '?section_id=cart-drawer')).text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#cart-drawer');
      if (fresh) drawer.replaceWith(fresh);
    } catch (e) { /* count already updated; drawer markup re-render best-effort */ }
    return cart;
  }

  async function addToCart(form) {
    const btn = form.querySelector('[type="submit"]');
    const formData = new FormData(form);
    if (btn) { btn.disabled = true; btn.dataset.label = btn.textContent; btn.textContent = 'Adding…'; }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      await refreshCartDrawer();
      openDrawer(document.getElementById('cart-drawer'));
    } catch (err) {
      const note = form.querySelector('[data-add-error]');
      if (note) note.textContent = err.message;
      else alert(err.message);
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || 'Add to cart'; }
    }
  }

  async function changeLine(key, quantity) {
    const drawer = document.getElementById('cart-drawer');
    const line = drawer && drawer.querySelector('[data-line-key="' + key + '"]');
    if (line) line.classList.add('cart-line-loading');
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    await refreshCartDrawer();
  }

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form.matches('[data-add-to-cart]')) {
      e.preventDefault();
      addToCart(form);
    }
  });

  document.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('[data-line-change]');
    if (qtyBtn) {
      e.preventDefault();
      changeLine(qtyBtn.getAttribute('data-line-key'), Number(qtyBtn.getAttribute('data-line-change')));
    }
    const removeBtn = e.target.closest('[data-line-remove]');
    if (removeBtn) {
      e.preventDefault();
      changeLine(removeBtn.getAttribute('data-line-key'), 0);
    }
    // Quick add buttons on cards
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      quickAdd(quick.getAttribute('data-quick-add'), quick);
    }
  });

  async function quickAdd(variantId, btn) {
    const label = btn.textContent;
    btn.textContent = 'Adding…'; btn.disabled = true;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      await refreshCartDrawer();
      openDrawer(document.getElementById('cart-drawer'));
    } finally {
      btn.textContent = label; btn.disabled = false;
    }
  }

  /* ─────────────── PDP: variant + gallery + qty ─────────────── */
  function initProductForm(root) {
    const data = root.querySelector('[data-variants-json]');
    if (!data) return;
    const variants = JSON.parse(data.textContent);
    const optionInputs = $$('[data-option-index]', root);
    const idField = root.querySelector('[name="id"]');
    const priceEl = root.querySelector('[data-price]');
    const addBtn = root.querySelector('[data-add-to-cart] [type="submit"]');

    function selectedOptions() {
      const opts = [];
      optionInputs.forEach((inp) => {
        if (inp.type === 'radio') { if (inp.checked) opts[inp.dataset.optionIndex] = inp.value; }
        else opts[inp.dataset.optionIndex] = inp.value;
      });
      return opts;
    }

    function update() {
      const sel = selectedOptions();
      const match = variants.find((v) => v.options.every((o, i) => o === sel[i]));
      if (!match) return;
      if (idField) idField.value = match.id;
      if (priceEl) priceEl.textContent = money(match.price);
      if (addBtn) {
        addBtn.disabled = !match.available;
        addBtn.textContent = match.available ? (addBtn.dataset.addLabel || 'Add to cart') : 'Sold out';
      }
      // reflect chosen swatch labels
      $$('[data-option-current]', root).forEach((el) => {
        el.textContent = sel[Number(el.getAttribute('data-option-current'))] || '';
      });
    }
    optionInputs.forEach((inp) => inp.addEventListener('change', update));
    update();

    // qty stepper
    const qtyInput = root.querySelector('[name="quantity"]');
    $$('[data-qty-step]', root).forEach((b) => b.addEventListener('click', () => {
      const delta = Number(b.getAttribute('data-qty-step'));
      qtyInput.value = Math.max(1, (Number(qtyInput.value) || 1) + delta);
    }));

    // gallery thumbnails
    $$('[data-thumb]', root).forEach((t) => t.addEventListener('click', () => {
      const main = root.querySelector('[data-main-media]');
      $$('[data-thumb]', root).forEach((x) => x.classList.remove('active'));
      t.classList.add('active');
      if (main) main.innerHTML = t.querySelector('[data-media-src]').innerHTML;
    }));
  }
  $$('[data-product-form]').forEach(initProductForm);

  /* ─────────────── Predictive search ─────────────── */
  const searchInput = $('[data-predictive-input]');
  if (searchInput) {
    let timer;
    const resultsEl = $('[data-predictive-results]');
    searchInput.addEventListener('input', () => {
      clearTimeout(timer);
      const q = searchInput.value.trim();
      if (!q) { if (resultsEl) resultsEl.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        const url = '/search/suggest?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=6&section_id=predictive-search';
        try {
          const html = await (await fetch(url)).text();
          const frag = new DOMParser().parseFromString(html, 'text/html');
          const inner = frag.querySelector('[data-predictive-results]');
          if (resultsEl && inner) resultsEl.innerHTML = inner.innerHTML;
        } catch (e) { /* keep last results */ }
      }, 200);
    });
  }

  /* ─────────────── Theme light/dark toggle ─────────────── */
  $$('[data-theme-toggle]').forEach((btn) => btn.addEventListener('click', () => {
    const root = document.documentElement;
    const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
    root.setAttribute('data-theme', next);
    try { localStorage.setItem('clla-theme', next); } catch (e) {}
  }));
  try {
    const saved = localStorage.getItem('clla-theme');
    if (saved) document.documentElement.setAttribute('data-theme', saved);
  } catch (e) {}

  /* Initial cart count sync */
  fetchCart().then(renderCartCount).catch(() => {});
})();
